import * as React from "react";
import { cn } from "../../../utils/classMerger";
import { useZapcapUsage } from "../../../hooks/useZapcapUsage";

interface CaptioningUsageBarProps {
    className?: string;
}

/**
 * Renders captioning seconds as minutes (+seconds only when there's a remainder):
 * 1200 → "20m", 1230 → "20m 30s", 30 → "0m 30s".
 */
const formatDuration = (totalSeconds: number): string => {
    const rounded = Math.max(0, Math.round(totalSeconds));
    const minutes = Math.floor(rounded / 60);
    const seconds = rounded % 60;
    return seconds === 0 ? `${minutes}m` : `${minutes}m ${seconds}s`;
};

/**
 * Compact ZapCap (video captioning) usage meter for the captioned-video upload
 * flow. Reads usage via useZapcapUsage and renders a placeholder ("— / —") until
 * the request settles, so the surrounding header reserves space and never reflows.
 * The progress bar is hidden below 640px; the numbers carry the info on mobile.
 */
const CaptioningUsageBar: React.FC<CaptioningUsageBarProps> = ({ className }) => {
    const { usage } = useZapcapUsage();

    const usedSeconds = usage?.usedSeconds ?? 0;
    const limitSeconds = usage?.limitSeconds ?? 0;
    const ratio = limitSeconds > 0 ? Math.min(usedSeconds / limitSeconds, 1) : 0;
    const pct = Math.round(ratio * 100);
    const isFull = !!usage && usedSeconds >= limitSeconds;
    const isNear = !isFull && ratio >= 0.8;
    // Approved-palette traffic-light states (see tailwind skill).
    const barColor = isFull
        ? "bg-[#c62828]"
        : isNear
        ? "bg-[#ffbe00]"
        : "bg-[#43a047]";

    return (
        <div
            className={cn(
                "flex items-center gap-2 min-[640px]:max-w-[320px] min-[640px]:flex-1 min-[640px]:justify-end",
                className
            )}
            title="Video captioning minutes used"
        >
            <span className="text-xs font-medium text-primary whitespace-nowrap">
                Captioning Usage
            </span>
            <div className="relative h-1.5 min-w-[48px] flex-1 overflow-hidden rounded-full bg-gray-200 max-[640px]:hidden">
                {usage && (
                    <div
                        className={cn("h-full rounded-full", barColor)}
                        style={{
                            width: `${usedSeconds > 0 ? Math.max(pct, 4) : 0}%`,
                        }}
                    />
                )}
            </div>
            <span className="ml-auto text-xs font-semibold text-primary tabular-nums whitespace-nowrap min-[640px]:ml-0">
                {usage
                    ? `${formatDuration(usedSeconds)} / ${formatDuration(
                          limitSeconds
                      )}`
                    : "— / —"}
            </span>
        </div>
    );
};

export default CaptioningUsageBar;
